import React, { Component, ErrorInfo, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { RefreshCcw, Home, Wrench, AlertCircle } from 'lucide-react';
import * as Sentry from "@sentry/react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    Sentry.captureException(error, { extra: { errorInfo } });
  }

  private handleReset = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen gradient-mesh flex items-center justify-center p-4 md:p-6 overflow-hidden">
          <motion.div 
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ 
                duration: 0.5, 
                ease: [0.25, 0.46, 0.45, 0.94] 
            }}
            className="w-full max-w-sm md:max-w-md bg-card rounded-[2.5rem] shadow-elevated border border-border/50 overflow-hidden"
          >
            <div className="p-8 md:p-12 text-center flex flex-col items-center">
              {/* Premium Icon Illustration */}
              <div className="relative mb-10 group">
                {/* Outer Glow */}
                <div className="absolute inset-0 bg-primary/15 blur-[40px] rounded-full scale-150 animate-pulse" />
                
                {/* Shield/Circle */}
                <div className="relative flex items-center justify-center h-28 w-28 md:h-32 md:w-32 bg-white/50 backdrop-blur-sm rounded-full border border-primary/20 shadow-card">
                  <div className="relative">
                    <Wrench className="h-12 w-12 md:h-14 md:w-14 text-primary" />
                    <motion.div
                      animate={{ 
                        rotate: [0, 45, -45, 0],
                        scale: [1, 1.15, 1]
                      }}
                      transition={{ 
                        repeat: Infinity, 
                        duration: 4,
                        ease: "easeInOut" 
                      }}
                      className="absolute -top-1 -right-1"
                    >
                      <AlertCircle className="h-6 w-6 md:h-7 md:w-7 text-primary fill-background shadow-sm" />
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Text Content */}
              <h1 className="text-2xl md:text-3xl font-extrabold text-foreground mb-3 font-sans tracking-tight">
                Service Interruption
              </h1>
              <p className="text-sm md:text-base text-muted-foreground mb-10 leading-relaxed font-medium">
                We encountered an unexpected error. Your data is safe—please refresh to continue.
              </p>

              {/* Action Buttons */}
              <div className="w-full space-y-4">
                <motion.button 
                  whileTap={{ scale: 0.96 }}
                  whileHover={{ scale: 1.02 }}
                  onClick={this.handleReset}
                  className="w-full h-14 md:h-16 rounded-full bg-primary text-primary-foreground font-bold shadow-primary-glow flex items-center justify-center gap-2 transition-all text-base md:text-lg"
                >
                  <RefreshCcw className="h-5 w-5 md:h-6 md:w-6" />
                  Refresh App
                </motion.button>
                
                <button 
                  onClick={() => window.location.href = '/'}
                  className="w-full h-12 rounded-full text-muted-foreground hover:text-foreground font-semibold flex items-center justify-center gap-2 hover:bg-secondary transition-colors text-sm"
                >
                  <Home className="h-4 w-4" />
                  Go Home
                </button>
              </div>

              {/* Subtle Tech Detail */}
              <div className="mt-10 pt-6 border-t border-border/40 w-full">
                <div className="flex items-center justify-center gap-2">
                  <span className="h-1 w-1 bg-primary rounded-full animate-ping" />
                  <p className="text-[10px] text-muted-foreground/50 font-mono uppercase tracking-[0.2em]">
                    System Ref ID: FE-902-SR
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
